import React, { useState, KeyboardEvent, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import "./share-template-popup.scss";

interface ShareTemplatePopupProps {
  templateName?: string;
  open: boolean;
  onClose: () => void;
  onShare: (emailsArray: string[]) => void;
}

const emailRegex = /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const ShareTemplatePopup: React.FC<ShareTemplatePopupProps> = ({
  open,
  templateName,
  onClose,
  onShare,
}) => {
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);

  const handleAddEmail = (value: string) => {
    const email = value.trim();

    if (!emailRegex.test(email)) return;
    if (emails.includes(email)) return;

    setEmails((prev) => [...prev, email]);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddEmail(emailInput);
      setEmailInput("");
    }
  };

  const handleDelete = (email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleShare = () => {
    onShare(emails);
  };

  useEffect(() => {
    if(open){
      setEmails([]);
      setEmailInput("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            padding: "0px !important",
            width: "600px",
            maxWidth: "95vw",
            borderRadius: "16px",
            overflowX: "hidden",
          },
        },
      }}
    >
      <DialogTitle className="dialog-title-share">
        Share Template
        <IconButton onClick={onClose} className="close-icon">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          overflowX: "hidden",
          padding: "10px 3px 10px 3px",
        }}
      >
        <Typography className="input-label">Template Name</Typography>

        <div className="input-template-share">
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter template name"
            size="small"
            name="providerId"
            value={templateName}
            className="input-template-share"
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "8px",
                },
                "&:hover fieldset": {
                  borderRadius: "8px",
                },
                "&.Mui-focused fieldset": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </div>

        <Typography className="input-label">Add People</Typography>

        <Box className="email-box">
          <MailOutlineIcon className="mail-icon" />

          {emails.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => handleDelete(email)}
              className="email-chip"
            />
          ))}

          <input
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type email and press Enter"
          />
        </Box>
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button onClick={onClose} color="inherit" sx={{ borderRadius: "8px" }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          className="share-btn"
          disabled={!emails.length}
          onClick={handleShare}
        >
          Share Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareTemplatePopup;
